/**
 * Finish Picker Web Component
 * Handles variant selection via Section Rendering API.
 * Listens for radio input changes AND select dropdown changes,
 * fetches re-rendered section HTML, and updates 8 DOM targets:
 * gallery, price, variant ID, ATC state, Affirm amount, sticky buy bar,
 * URL, and option availability.
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABFinishPicker extends ABBaseComponent {
  connectedCallback() {
    this.addEventListener('change', this.onOptionChange.bind(this));
  }

  async onOptionChange(event) {
    // Handle finish dropdown select
    if (event.target.matches('select[data-finish-dropdown]')) {
      const selectedOptionValueId = event.target.value;
      const radio = this.querySelector(`input[data-option-value-id="${selectedOptionValueId}"]`);
      if (radio) {
        radio.checked = true;
        const fieldset = radio.closest('fieldset');
        const selectedSpan = fieldset?.querySelector('[data-selected-value]');
        if (selectedSpan) selectedSpan.textContent = radio.value;
      }
      // Fall through to Section Rendering API flow below
    } else if (event.target.matches('input[type="radio"]')) {
      // Sync dropdown when radio changes
      const dropdown = this.querySelector('select[data-finish-dropdown]');
      if (dropdown && event.target.dataset.optionValueId) {
        dropdown.value = event.target.dataset.optionValueId;
      }

      // Update selected value display text
      const fieldset = event.target.closest('fieldset');
      const selectedSpan = fieldset?.querySelector('[data-selected-value]');
      if (selectedSpan) selectedSpan.textContent = event.target.value;
    } else {
      return;
    }

    // Update swatch is-active class to follow selection
    const changedInput = event.target.matches('input[type="radio"]')
      ? event.target
      : this.querySelector('input[type="radio"]:checked');
    if (changedInput) {
      const fieldset = changedInput.closest('fieldset');
      if (fieldset) {
        fieldset.querySelectorAll('.ab-finish-swatch').forEach(s => s.classList.remove('is-active'));
        changedInput.closest('label')?.querySelector('.ab-finish-swatch')?.classList.add('is-active');
      }
    }

    // 1. Collect all selected option value IDs
    const selectedIds = Array.from(this.querySelectorAll('input[type="radio"]:checked'))
      .map(input => input.dataset.optionValueId)
      .filter(Boolean);

    if (selectedIds.length === 0) return;

    // 2. Fetch updated section via Section Rendering API
    const sectionEl = this.closest('.shopify-section');
    const productUrl = sectionEl?.querySelector('[data-product-url]')?.dataset.productUrl
      || window.location.pathname;
    const url = `${productUrl}?section_id=${this.sectionId}&option_values=${selectedIds.join(',')}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // 3. Replace gallery
      const newGallery = doc.querySelector('ab-product-gallery');
      const currentGallery = sectionEl.querySelector('ab-product-gallery');
      if (newGallery && currentGallery) {
        currentGallery.innerHTML = newGallery.innerHTML;
      }

      // 4. Replace price area
      const newPrice = doc.querySelector('[data-product-price]');
      const currentPrice = sectionEl.querySelector('[data-product-price]');
      if (newPrice && currentPrice) {
        currentPrice.innerHTML = newPrice.innerHTML;
      }

      // 4b. Replace SKU line
      const newSku = doc.querySelector('[data-product-sku]');
      const currentSku = sectionEl.querySelector('[data-product-sku]');
      if (newSku && currentSku) {
        currentSku.textContent = newSku.textContent;
        currentSku.hidden = newSku.hidden;
      }

      // 5. Update hidden variant ID
      const newVariantInput = doc.querySelector('input[name="id"]');
      const currentVariantInput = sectionEl.querySelector('input[name="id"]');
      if (newVariantInput && currentVariantInput) {
        currentVariantInput.value = newVariantInput.value;
      }

      // 6. Update ATC button state (available/sold out)
      const newAtc = doc.querySelector('.ab-product__atc');
      const currentAtc = sectionEl.querySelector('.ab-product__atc');
      if (newAtc && currentAtc) {
        currentAtc.disabled = newAtc.disabled;
        currentAtc.textContent = newAtc.textContent;
      }

      // 7. Update Affirm amount
      const newAffirm = doc.querySelector('.affirm-as-low-as');
      const currentAffirm = sectionEl.querySelector('.affirm-as-low-as');
      if (newAffirm && currentAffirm) {
        currentAffirm.dataset.amount = newAffirm.dataset.amount;
        window.affirm?.ui?.refresh?.();
      }

      // 8. Update sticky buy bar price and finish name
      const newStickyPrice = doc.querySelector('.ab-sticky-buy-bar [data-sticky-price]');
      const currentStickyPrice = sectionEl.querySelector('.ab-sticky-buy-bar [data-sticky-price]');
      if (newStickyPrice && currentStickyPrice) {
        currentStickyPrice.innerHTML = newStickyPrice.innerHTML;
      }
      const newStickyFinish = doc.querySelector('.ab-sticky-buy-bar [data-sticky-finish]');
      const currentStickyFinish = sectionEl.querySelector('.ab-sticky-buy-bar [data-sticky-finish]');
      if (newStickyFinish && currentStickyFinish) {
        currentStickyFinish.textContent = newStickyFinish.textContent;
      }

      // 9. Update URL without reload
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('option_values', selectedIds.join(','));
      history.replaceState({}, '', newUrl);

      // 10. Update option availability states from re-rendered HTML
      const changedFieldset = event.target.closest('fieldset');
      const newPicker = doc.querySelector('ab-finish-picker');
      if (newPicker) {
        const currentFieldsets = this.querySelectorAll('fieldset');
        const newFieldsets = newPicker.querySelectorAll('fieldset');
        currentFieldsets.forEach((fs, i) => {
          if (fs === changedFieldset) return; // Skip the one user just changed
          const newFs = newFieldsets[i];
          if (!newFs) return;
          fs.querySelectorAll('input[type="radio"]').forEach(input => {
            const newInput = newFs.querySelector(`input[data-option-value-id="${input.dataset.optionValueId}"]`);
            if (newInput) {
              input.disabled = newInput.disabled;
              input.closest('label')?.classList.toggle('ab-product__option--unavailable', newInput.disabled);
            }
          });
        });
      }

      // 11. Sync dropdown after Section Rendering DOM swap
      // The new <select> from server has correct `selected` attribute, but
      // we replaced option availability, not the whole picker. Sync dropdown
      // to match currently checked radio.
      const dropdown = this.querySelector('select[data-finish-dropdown]');
      if (dropdown) {
        const checkedFinishRadio = this.querySelector('fieldset input[type="radio"]:checked');
        if (checkedFinishRadio?.dataset.optionValueId) {
          dropdown.value = checkedFinishRadio.dataset.optionValueId;
        }
      }

    } catch (error) {
      console.error('Variant update failed:', error);
    }
  }
}

defineComponent('ab-finish-picker', ABFinishPicker);
